import { StarIcon } from "@heroicons/react/24/solid"
import React from "react"

import Typography from "./shared/Typography"

// Request higher-resolution image from Filestack to avoid pixelation on retina (2x for max card width 288px)
const LISTING_CARD_IMAGE_WIDTH_2X = 576

function getListingCardImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") return imageUrl
  // Only transform simple Filestack handle URLs (no existing transform in path)
  const match = imageUrl.match(/^(https:\/\/cdn\.filestackcontent\.com)\/([^/]+)$/)
  if (match) {
    const [, base, handle] = match
    return `${base}/resize=width:${LISTING_CARD_IMAGE_WIDTH_2X},fit:max/${handle}`
  }
  return imageUrl
}

const ListingCard = ({ data, isPreview = false, mobile = false, maxWidth = null }) => {
  // Defensive destructuring
  const { practice = {}, listingCardSettings = {}, isVirtual = false } = data || {}

  // If listing card headline is empty, use title + headline (if headline exists)
  // Limit headline to 40 characters for conciseness
  const rawHeadline =
    listingCardSettings.headline ||
    (practice.title && practice.headline
      ? `${practice.title} - ${practice.headline}`
      : practice.title || practice.headline || "Your Headline Here")
  const headline = rawHeadline.length > 40 ? `${rawHeadline.substring(0, 40)}...` : rawHeadline
  const imageUrl = listingCardSettings.imageUrl || practice.defaultPhotoUrl
  const displayName = practice.displayName || "Practitioner Name"
  const locationText = isVirtual
    ? "Zoom"
    : practice.cityState || (practice.city ? `${practice.city}, ${practice.stateAbbreviation}` : "Location, ST")

  // Prices logic: "From $XX" using lowest cost service (excluding free)
  const showPrice = practice.cheapestServiceAmount && practice.cheapestServiceAmount > 0
  const priceDisplay = showPrice ? `From $${Math.round(practice.cheapestServiceAmount)}` : "Price upon request"

  // Determine max width based on prop or defaults
  const getMaxWidth = () => {
    if (maxWidth) return maxWidth
    return mobile ? "168px" : "288px"
  }

  const getMinWidth = () => (mobile ? "156px" : "256px")

  return (
    <div
      className={`relative mx-auto flex flex-col items-start gap-3 ${mobile ? "" : "lg:mx-0"}`}
      style={{ minWidth: getMinWidth(), maxWidth: getMaxWidth() }}>
      {isPreview && (
        <div className="flex h-6 shrink-0 items-center justify-center rounded-lg bg-gray-light px-2.5 py-0">
          <span className="font-manrope text-[12px] font-extrabold uppercase leading-6 tracking-[0.72px] text-[#0b3954]">
            Preview
          </span>
        </div>
      )}
      <div
        className={`relative flex w-full shrink-0 flex-col items-start overflow-hidden rounded-lg bg-white text-black shadow-[0px_8px_24px_0px_rgba(11,57,84,0.08)] ${
          !mobile ? "transition-shadow group-hover:shadow-[0px_12px_32px_0px_rgba(11,57,84,0.16)]" : ""
        }`}>
        {/* Main Image - 3:2 aspect ratio matches card so cropping is consistent */}
        <div
          className={`aspect-[3/2] w-full shrink-0 overflow-hidden bg-gray-ultralight ${
            mobile ? "rounded-t-[8px]" : ""
          }`}>
          {imageUrl && (
            <img
              src={getListingCardImageUrl(imageUrl)}
              alt={displayName}
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
            />
          )}
        </div>

        {/* Content Area */}
        <div
          className={`relative flex w-full shrink-0 flex-col items-start border-x border-b border-gray-light bg-white ${
            mobile ? "rounded-b-[8px] border-t-0 px-[16px] py-[8px]" : "rounded-b-lg p-4"
          }`}>
          {mobile ? (
            // Mobile Layout: Rating -> Headline -> Name/Location/Price
            <div className="relative flex w-full shrink-0 flex-col items-start gap-[4px] text-left">
              {/* Reviews - First in mobile */}
              <div className="flex h-[20px] w-full items-center gap-[4px] overflow-hidden">
                <StarIcon className="h-[20px] w-[20px] shrink-0 text-orange" />
                {practice.reviewsCount > 0 ? (
                  <span className="truncate font-manrope text-[14px] font-bold leading-[20px] text-[#0b3954]">
                    {practice.reviewsAverage?.toFixed(1) || "0.0"}
                  </span>
                ) : (
                  <span className="truncate font-manrope text-[14px] font-bold leading-[20px] text-[#0b3954]">
                    No reviews yet
                  </span>
                )}
              </div>

              {/* Headline - Single line, max 40 characters */}
              <div
                className="w-full truncate font-manrope text-[16px] font-bold leading-[24px] text-[#0b3954]"
                style={{
                  minHeight: "24px",
                  maxHeight: "24px"
                }}
                title={headline}>
                {headline}
              </div>

              {/* From $XX (above name) | Name | Location */}
              <div className="relative flex w-full shrink-0 flex-col items-start gap-1">
                <p className="w-full truncate font-manrope text-[14px] font-semibold leading-[20px] text-[#3d5a6a]">
                  {priceDisplay}
                </p>
                <p className="w-full truncate font-manrope text-[14px] font-normal leading-[20px] text-[#55778a]">
                  {displayName}
                </p>
                <p className="flex w-full items-center gap-1 truncate font-manrope text-[12px] font-normal leading-[20px] text-[#55778a]">
                  {isVirtual && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0">
                      <path
                        d="M7.5 4.5L10.1056 3.19721C10.4741 3.01295 10.9 3.28378 10.9 3.69443V8.30557C10.9 8.71622 10.4741 8.98705 10.1056 8.80279L7.5 7.5V4.5Z"
                        fill="#55778A"
                      />
                      <rect x="0.5" y="2.5" width="7.5" height="7" rx="1.5" fill="#55778A" />
                    </svg>
                  )}
                  {locationText}
                </p>
              </div>
            </div>
          ) : (
            // Desktop Layout: Headline -> Price (above name) -> Avatar + Name + Location -> Reviews
            <div className="relative flex w-full shrink-0 flex-col items-start gap-2 text-left">
              {/* Headline - Single line, max 40 characters */}
              <div
                className="mb-0 w-full truncate text-lg font-bold leading-6 text-black"
                style={{
                  minHeight: "24px",
                  maxHeight: "24px"
                }}
                title={headline}>
                {headline}
              </div>

              {/* Price - above name, in-between size per design */}
              <p className="mb-0 w-full truncate font-manrope text-[14px] font-semibold leading-[20px] text-[#3d5a6a]">
                {priceDisplay}
              </p>

              {/* Avatar + Name + Location */}
              <div className="relative flex h-[40px] w-full shrink-0 items-center gap-2 overflow-hidden">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-ultralight">
                  {practice.defaultPhotoUrl && (
                    <img
                      src={getListingCardImageUrl(practice.defaultPhotoUrl)}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="relative flex min-w-0 shrink flex-col items-start gap-0.5">
                  <Typography variant="small" className="mb-0 w-full truncate font-bold text-black">
                    {displayName}
                  </Typography>
                  <Typography variant="small" className="mb-0 flex w-full items-center gap-1 truncate text-gray-dark">
                    {isVirtual && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0">
                        <path
                          d="M7.5 4.5L10.1056 3.19721C10.4741 3.01295 10.9 3.28378 10.9 3.69443V8.30557C10.9 8.71622 10.4741 8.98705 10.1056 8.80279L7.5 7.5V4.5Z"
                          fill="currentColor"
                        />
                        <rect x="0.5" y="2.5" width="7.5" height="7" rx="1.5" fill="currentColor" />
                      </svg>
                    )}
                    {locationText}
                  </Typography>
                </div>
              </div>

              <hr className="my-0 h-[1px] w-full border-gray-light" />

              {/* Reviews */}
              <div className="flex h-[20px] w-full items-center gap-1.5 overflow-hidden">
                <StarIcon className="h-4 w-4 shrink-0 text-orange" />
                {practice.reviewsCount > 0 ? (
                  <div className="flex min-w-0 items-center gap-2.5 overflow-hidden">
                    <Typography variant="small" className="mb-0 shrink-0 font-bold text-black">
                      {practice.reviewsAverage?.toFixed(1) || "0.0"}
                    </Typography>
                    <Typography variant="small" className="mb-0 truncate text-gray-dark">
                      {practice.reviewsCount} Verified Reviews
                    </Typography>
                  </div>
                ) : (
                  <Typography variant="small" className="mb-0 truncate font-bold text-black">
                    No reviews yet
                  </Typography>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ListingCard
